// eslint-disable-next-line no-use-before-define
import React from 'react';
import { Week001LeaderboardResponseData, Week001MyScoreResponseData } from '$/services/api_service/apis/work.api';
import { LeaderboardPlayerAvatar } from './leaderboard-player-avatar';

// Unity timeTaken and the weekly leaderboard store seconds, like the room results.
export function formatChallengeTime(seconds?: number): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '\u2014';
  const hundredths = Math.round(seconds * 100);
  return [Math.floor(hundredths / 6000), Math.floor(hundredths / 100) % 60, hundredths % 100].map((part) => String(part).padStart(2, '0')).join(':');
}

export const LeaderboardPersonalResults = ({
  personal, loading, personalError, rankLabel = 'Rank',
}: { personal: Week001MyScoreResponseData, loading: boolean, personalError: boolean, rankLabel?: string }) => {
  const leadingCategories = !loading && !personalError ? [personal?.myFastest?.ranking === 0 ? 'Fastest' : '', personal?.myCoder?.ranking === 0 ? 'Coder' : ''].filter(Boolean) : [];
  return (
    <section className={`weekly-personal ${leadingCategories.length ? 'weekly-personal-winner' : ''}`} aria-label="Your best results">
      <div>
        <strong>
          {leadingCategories.length > 0 && <i className="fas fa-trophy weekly-personal-trophy" aria-hidden="true" />}
          Your best result
        </strong>
        <p>{leadingCategories.length ? `Well done! You're #1 in ${leadingCategories.join(' and ')}!` : personalError ? 'Your results could not be loaded.' : personal?.myFastest || personal?.myCoder ? 'Keep going. Your next run could be your best.' : 'Finish a run to set your first score.'}</p>
      </div>
      <div className="weekly-personal-scores">
        <div>
          <span>Time</span>
          <strong>{formatChallengeTime(personal?.myFastest?.score)}</strong>
          {personal?.myFastest && (
          <small>
            {rankLabel}
            {' '}
            {personal.myFastest.ranking + 1}
          </small>
          )}
        </div>
        <div>
          <span>Coding score</span>
          <strong>{personal?.myCoder?.score ?? '—'}</strong>
          {personal?.myCoder && (
          <small>
            {rankLabel}
            {' '}
            {personal.myCoder.ranking + 1}
          </small>
          )}
        </div>
      </div>
    </section>
  );
};

LeaderboardPersonalResults.defaultProps = { rankLabel: 'Rank' };

export const LeaderboardResults = ({
  scores, loading, error, footerControls,
}: { scores: Week001LeaderboardResponseData, loading: boolean, error: string, footerControls?: React.ReactNode }) => {
  const [category, setCategory] = React.useState<'fastest' | 'coder'>('fastest');
  const rows = (category === 'fastest' ? scores?.fastestLeaderboard : scores?.coderLeaderboard) || [];
  const players = rows.slice(0, 10).map((row, index) => {
    let value: any[] = []; try { const parsed = JSON.parse(row.value || '[]'); if (Array.isArray(parsed)) value = parsed; } catch (failure) { /* Keep a readable fallback for malformed historical scores. */ } return {
      id: `${value[0] || 'player'}:${index}`, name: value[1] || 'Unknown player', room: value[2], score: row.score,
    };
  });

  return (
    <section className="weekly-rankings">
      <header>
        <div>
          <h3>Leaderboard</h3>
          <p>Top 10 in each category</p>
        </div>
        <div className="ranking-tabs" aria-label="Leaderboard category">{(['fastest', 'coder'] as const).map((value) => <button type="button" aria-pressed={category === value} className={category === value ? 'active' : ''} key={value} onClick={() => setCategory(value)}>{value === 'fastest' ? 'Fastest' : 'Coder'}</button>)}</div>
      </header>
      <p className="ranking-explainer">{category === 'fastest' ? 'The shortest finish time takes the top spot.' : 'The highest coding score takes the top spot.'}</p>
      {loading && <p role="status" className="ranking-explainer">Loading results...</p>}
      {error && <p role="alert" className="text-danger">{error}</p>}
      {!loading && !error && !players.length && <p className="projects-empty">No results yet. Set the first score!</p>}
      {!error && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to scroll this region.
        <div className="weekly-table-scroll" tabIndex={0} role="region" aria-label="Leaderboard players">
          <table className="weekly-table" role="table">
            <caption className="visually-hidden">
              {category === 'fastest' ? 'Fastest' : 'Coder'}
              {' '}
              leaderboard
            </caption>
            <thead>
              <tr role="row">
                <th scope="col" role="columnheader">Rank</th>
                <th scope="col" role="columnheader">Player</th>
                <th scope="col" role="columnheader">{category === 'fastest' ? 'Time' : 'Score'}</th>
              </tr>
            </thead>
            <tbody>
              {players.map((player, index) => (
                <tr role="row" key={player.id} className={index === 0 ? 'weekly-winner' : ''}>
                  <td>
                    <span className="weekly-rank">
                      {index === 0 ? (
                        <>
                          <i className="fas fa-trophy" aria-hidden="true" />
                          <span className="visually-hidden">1</span>
                        </>
                      ) : index + 1}
                    </span>
                  </td>
                  <td>
                    <LeaderboardPlayerAvatar name={String(player.name)} />
                    <span className="weekly-player-name">
                      {player.name}
                    </span>
                  </td>
                  <td>{category === 'fastest' ? formatChallengeTime(player.score) : player.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <footer>
        <span>{`Showing ${players.length} of ${Math.min(rows.length, 10)} players`}</span>
        {footerControls}
      </footer>
    </section>
  );
};

LeaderboardResults.defaultProps = { footerControls: undefined };
